import { useCallback, useState } from 'react';

import { snackbar, ConfirmDialog, LinearProgress, Typography } from '@occc-app/design-system';
import {
  RetailerNetworkLocationGroupFilter,
  RetailerNetworkLocationGroupFragment,
} from '@occc/graphql';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import { LocationGroupsTable } from './location-groups-table.component';
import { AFTER_PARAM_NAME, BEFORE_PARAM_NAME } from './retailer-network-location-groups.constants';

import { useRetailerNetworkLocationGroupDeleteMutation } from '../../../data-access/mutations';
import { useGetRetailerNetworkLocationGroups } from '../../../data-access/queries';
import { LocationGroupUpdateDialog } from '../../components/location-group-update-dialog/location-group-update-dialog.component';

type LocationGroupsTableContainerProps = {
  retailerNetworkUuid: string;
  filter: RetailerNetworkLocationGroupFilter;
};

export const LocationGroupsTableContainer = ({
  retailerNetworkUuid,
  filter,
}: LocationGroupsTableContainerProps) => {
  const [groupToEdit, setGroupToEdit] = useState<null | RetailerNetworkLocationGroupFragment>(null);
  const [groupToDelete, setGroupToDelete] = useState<null | RetailerNetworkLocationGroupFragment>(
    null,
  );

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: BEFORE_PARAM_NAME,
    afterParamName: AFTER_PARAM_NAME,
  });

  const { loading, error, data, paginationProps } = useGetRetailerNetworkLocationGroups({
    filter,
    paginationInfo,
    onPageChange,
  });

  const handleCloseUpdateDialog = useCallback(() => {
    setGroupToEdit(null);
  }, []);

  const handleCloseDeleteConfirmationDialog = useCallback(() => {
    setGroupToDelete(null);
  }, []);

  const onRemoveLocationGroupComplete = useCallback(() => {
    snackbar.success('Locations Group has been successfully deleted from the network');
    handleCloseDeleteConfirmationDialog();
  }, [handleCloseDeleteConfirmationDialog]);

  const onRemoveLocationGroupError = useCallback(() => {
    snackbar.error('Unable to delete Location Group. Please try again later.');
  }, []);

  const [deleteLocationGroup, { loading: deleteLocationGroupLoading }] =
    useRetailerNetworkLocationGroupDeleteMutation({
      onCompleted: onRemoveLocationGroupComplete,
      onError: onRemoveLocationGroupError,
      refetchQueries: ['retailerNetwork', 'retailerNetworkLocationGroups'],
    });

  const onDeleteLocationGroupFromRetailerNetwork = async () => {
    if (!groupToDelete) {
      return;
    }

    deleteLocationGroup({
      variables: {
        retailerNetworkUuid,
        locationGroupUuid: groupToDelete.uuid,
      },
    });
  };

  return (
    <>
      <LocationGroupsTable
        retailerNetworkUuid={retailerNetworkUuid}
        locationGroups={data}
        loading={loading}
        error={error}
        paginationProps={paginationProps}
        onEdit={setGroupToEdit}
        onDelete={setGroupToDelete}
      />

      {groupToEdit && (
        <LocationGroupUpdateDialog
          locationGroup={groupToEdit}
          open={true}
          onClose={handleCloseUpdateDialog}
          retailerNetworkUuid={retailerNetworkUuid}
        />
      )}

      {groupToDelete && (
        <ConfirmDialog
          dialogTitle='Delete Location Group'
          onClose={handleCloseDeleteConfirmationDialog}
          open={true}
          maxWidth='xs'
          confirmButtonText='Delete'
          confirmButtonProps={{
            onClick: onDeleteLocationGroupFromRetailerNetwork,
            disabled: deleteLocationGroupLoading,
            color: 'error',
          }}
        >
          {deleteLocationGroupLoading && <LinearProgress />}
          <Typography>
            {`Are you sure you would like to delete "${groupToDelete.name}" Location Group? This will not delete the
            locations, only their group.`}
          </Typography>
        </ConfirmDialog>
      )}
    </>
  );
};
